import 'regenerator-runtime/runtime'; // Add this line at the top
import React, { useEffect, useState, useMemo, useCallback } from "react";
import SalesInsertBody from "./SalesInsertBody";
import { Button, Card, CardBody, CardHeader, Table, Row, Col } from "reactstrap";
import { getItemsdData, getPartyDetails, saveInvoiceData, getNextInvoiceNumber } from "../../util/APIUtils";
import { LoadingOverlay } from "../../util/Helpers";
import swal from 'sweetalert';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import GeneratePDF from './GeneratePDF';
import { ButtonStyles } from "../../constants/ButtonStyles";

export default function SaleInsert() {
  const [values, setValues] = useState({
    invocieDate: new Date().toISOString().substring(0, 10),
    invoiceNum: "",
  });
  const [block, setBlock] = useState(false);
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState([]);
  const [partyDetails, setPartyDetails] = useState([]);

  useEffect(() => {
    loadReqData();
  }, []);

  const clearAll = useCallback(async () => {
    let nextNum = "";
    try {
      const resp = await getNextInvoiceNumber();
      if (resp && resp.nextInvoiceNumber) nextNum = resp.nextInvoiceNumber;
    } catch (err) {
      console.warn("Failed to fetch next invoice number:", err);
    }
    setValues({
      invocieDate: new Date().toISOString().substring(0, 10),
      customerId: "",
      item: "",
      invoiceNum: nextNum,
    });
    setSelectedItem([]);
  }, []);

  const loadReqData = useCallback(async () => {
    setBlock(true);
    try {
      const [itemsResponse, partyDetailsResponse, nextNumResp] = await Promise.all([
        getItemsdData(),
        getPartyDetails(),
        getNextInvoiceNumber(),
      ]);
      setItems(itemsResponse);
      setPartyDetails(partyDetailsResponse);
      if (nextNumResp && nextNumResp.nextInvoiceNumber) {
        setValues((prev) => ({ ...prev, invoiceNum: nextNumResp.nextInvoiceNumber }));
      }
    } catch (error) {
      swal({ text: `Unexpected Error: ${error.message}` });
    } finally {
      setBlock(false);
    }
  }, []);

  const onChangeForm = useCallback(({ target: { name, value } }) => {
    if (name === 'item') {
      const selectedItem = items.find((item) => item.id === value.id);
      if (selectedItem) {
        setValues((prevValues) => ({
          ...prevValues,
          ...selectedItem,
          unitprice: selectedItem.salePrice,
          unit: selectedItem.baseUnit,
          item: { value: selectedItem.id, label: selectedItem.name }
        }));
      }
    } else {
      setValues((prevValues) => ({
        ...prevValues,
        [name]: value,
      }));
    }
  }, [items]);

  const onchangeunit = useCallback(({ target: { name, value } }) => {
    const unitPrice = value === values.baseUnit ? values.salePrice : values.secondaryUnitSellingPrice;
    setValues((prevValues) => ({
      ...prevValues,
      [name]: value,
      unitprice: unitPrice,
    }));
  }, [values]);

  const validationMessages = useMemo(() => [
    { condition: !values.customerId, message: "Please select a party" },
    { condition: !values.invocieDate, message: "Please enter a valid invoice date" },
    { condition: !values.id, message: "Please select an item" },
    { condition: !values.quantity || values.quantity <= 0, message: "Please add a valid quantity" }
  ], [values]);

  const additem = useCallback(() => {
    for (const { condition, message } of validationMessages) {
      if (condition) {
        swal({ text: message });
        return false;
      }
    }
    const isItExists = selectedItem.some((itm) => itm.itemCode === values.itemCode);
    if (!isItExists) {
      const newItem = {
        ...values,
        total: values.quantity * values.unitprice
      };
      delete newItem.item;
      delete newItem.label;
      delete newItem.value;
      setSelectedItem((prevItems) => [...prevItems, newItem]);
      setValues({
        invocieDate: values.invocieDate,
        customerId: values.customerId,
        invoiceNum: values.invoiceNum,
        item: "",
      });
    } else {
      swal({ text: "Item Already exists" });
    }
  }, [validationMessages, selectedItem, values]);

  const deleteSelectedItm = useCallback((id) => {
    setSelectedItem((prevItems) => prevItems.filter((obj) => obj.id !== id));
  }, []);

  const saveItems = useCallback(async () => {
    setBlock(true);
    try {
      await saveInvoiceData(selectedItem);
      GeneratePDF(selectedItem);
      swal({ text: "Invoice created successfully" });
      setSelectedItem([]);
      await clearAll();
    } catch (error) {
      swal({ text: `Unexpected Error: ${error.message}` });
    } finally {
      setBlock(false);
    }
  }, [selectedItem, clearAll]);

  const totalQuantity = useMemo(() => selectedItem.reduce((acc, item) => acc + parseInt(item.quantity), 0), [selectedItem]);
  const totalAmount = useMemo(() => selectedItem.reduce((acc, item) => acc + parseFloat(item.total), 0), [selectedItem]);
  const memoizedPartyDetails = useMemo(() => partyDetails, [partyDetails]);
  const memoizedItems = useMemo(() => items, [items]);
  return (
    <div className="animated fadeIn">
      {block && <LoadingOverlay />}
      <SalesInsertBody
        additem={additem}
        onChangeForm={onChangeForm}
        clearAll={clearAll}
        items={memoizedItems}
        values={values}
        onchangeunit={onchangeunit}
        partyDetails={memoizedPartyDetails}
        disable={selectedItem.length === 0 ? false : true}
      />

      <Card style={{
        borderRadius: "15px",
        boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
        border: "none",
        overflow: "hidden",
        marginTop: "20px"
      }}>
        <CardHeader style={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          padding: "15px 25px",
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: "none"
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <i className="fa fa-shopping-cart" style={{ fontSize: "20px", color: "#fff" }}></i>
            <strong style={{ fontSize: '1rem', color: "#fff" }}>Selected Items</strong>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
            <span style={{ color: "rgba(255,255,255,0.9)", fontSize: "14px" }}>
              Count: <strong>{totalQuantity}</strong> | Total: <strong>{totalAmount.toFixed(2)}</strong>
            </span>
            <Button
              style={ButtonStyles.successSm}
              onClick={saveItems}
              disabled={selectedItem.length === 0}
            >
              <i className="fa fa-check" style={{ marginRight: "5px" }} />Submit
            </Button>
          </div>
        </CardHeader>
        <CardBody style={{ padding: "25px" }}>
          <div className="table-responsive">
            <Table id="invoice-table" bordered hover responsive>
              <thead>
                <tr>
                  <th>S.No.</th>
                  <th>Item</th>
                  <th>MRP</th>
                  <th>Quantity</th>
                  <th>Unit</th>
                  <th>Price per Unit</th>
                  <th>GST</th>
                  <th>Amount</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {selectedItem.map((itm, index) => (
                  <tr key={itm.id}>
                    <th scope="row">{index + 1}</th>
                    <td>{itm.name}</td>
                    <td>{itm.mrp}</td>
                    <td>{itm.quantity}</td>
                    <td>{itm.unit}</td>
                    <td>{parseFloat(itm.total)}</td>
                    <td>{itm.unit === 'Box' ? parseFloat(itm.quantity * itm.baseUnitGst) : parseFloat(itm.quantity * itm.secondaryUnitGst)}</td>
                    <td>{itm.total}</td>
                    <td>
                      <Button color="danger" size="sm" onClick={() => deleteSelectedItm(itm.id)}>
                        <i className="fa fa-close" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}


// WEBPACK FOOTER //
// ./src/components/CustomComponents/SaleInsert.js